import { useEffect, useState } from 'react';
import type { Photo } from '../api/photos';
import { deletePhoto, updatePhotoCaption } from '../api/photos';

type Props = {
  photos: Photo[];
  startIndex: number;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onCaptionChanged: (photo: Photo) => void;
};

export function PhotoLightbox({ photos, startIndex, onClose, onDeleted, onCaptionChanged }: Props) {
  const [index, setIndex] = useState(startIndex);
  const [editingCaption, setEditingCaption] = useState(false);
  const photo = photos[index];

  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) setIndex(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, photos.length, onClose]);

  if (!photo) return null;

  async function onDelete() {
    if (!photo) return;
    if (!confirm('Eliminare questa foto? Il file sul disco verrà cancellato.')) return;
    try {
      await deletePhoto(photo.id);
      onDeleted(photo.id);
      if (photos.length === 1) onClose();
      else if (index >= photos.length - 1) setIndex(index - 1);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    }
  }

  async function onCaptionSave(value: string) {
    if (!photo) return;
    try {
      const updated = await updatePhotoCaption(photo.id, value || null);
      onCaptionChanged(updated);
      setEditingCaption(false);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore caption');
    }
  }

  return (
    <div className="lightbox" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose} aria-label="Chiudi">
          ×
        </button>
        {index > 0 && (
          <button
            className="lightbox-nav prev"
            onClick={() => setIndex(index - 1)}
            aria-label="Precedente"
          >
            ‹
          </button>
        )}
        {index < photos.length - 1 && (
          <button
            className="lightbox-nav next"
            onClick={() => setIndex(index + 1)}
            aria-label="Successiva"
          >
            ›
          </button>
        )}

        <img src={photo.url} alt={photo.caption ?? ''} className="lightbox-img" />

        <div className="lightbox-meta">
          {editingCaption ? (
            <CaptionEditor
              initial={photo.caption ?? ''}
              onSave={onCaptionSave}
              onCancel={() => setEditingCaption(false)}
            />
          ) : (
            <p className="caption">
              {photo.caption ?? <span className="muted">(nessuna didascalia)</span>}{' '}
              <button
                type="button"
                className="link-btn"
                onClick={() => setEditingCaption(true)}
                title="Modifica didascalia"
              >
                ✏️
              </button>
            </p>
          )}

          <p className="meta-row">
            <span className="muted">
              {index + 1} / {photos.length}
            </span>
            {photo.takenAt && (
              <span className="muted">· scattata {formatDate(photo.takenAt)}</span>
            )}
            {photo.width && photo.height && (
              <span className="muted">
                · {photo.width}×{photo.height}
              </span>
            )}
            <span className="muted">· {(photo.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
            <button className="btn btn-danger lightbox-delete" onClick={() => void onDelete()}>
              Elimina
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function CaptionEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (v: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  return (
    <div className="caption-editor">
      <textarea
        value={v}
        onChange={(e) => setV(e.target.value)}
        rows={2}
        maxLength={2000}
        autoFocus
      />
      <div className="actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={saving}
        >
          Annulla
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={async () => {
            setSaving(true);
            await onSave(v.trim());
            setSaving(false);
          }}
          disabled={saving}
        >
          {saving ? 'Salva…' : 'Salva'}
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
